/**
 * Filter dashboard, rentang tanggal, dan penyusunan query ekspor — MURNI.
 *
 * Modul ini menjaga satu regresi klasik yang mahal:
 *
 * > **Tombol Ekspor mengabaikan filter yang sedang aktif di layar.**
 *
 * Akibatnya manajer yang melihat 12 baris mengunduh berkas berisi seluruh organisasi, lalu
 * mengirimkannya lewat surel karena ia mengira isinya sama dengan yang di layar. Ekspor adalah
 * berkas yang berpindah tangan; selisih antara yang terlihat dan yang terunduh adalah kebocoran,
 * bukan sekadar ketidaknyamanan.
 *
 * Karena itu query ekspor disusun dari objek filter YANG SAMA dengan yang dipakai memuat daftar,
 * bukan dirakit ulang di tempat tombol berada.
 */

export const MAX_SUMMARY_RANGE_DAYS = 31;

export interface DateRange {
  readonly from: string;
  readonly to: string;
}

export interface RangeProblem {
  readonly code: 'invalid_format' | 'inverted' | 'too_long';
  readonly message: string;
}

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Selisih hari INKLUSIF — 1..1 adalah 1 hari, bukan 0. */
export function daysBetweenInclusive(from: string, to: string): number {
  const start = Date.UTC(
    Number(from.slice(0, 4)),
    Number(from.slice(5, 7)) - 1,
    Number(from.slice(8, 10)),
  );
  const end = Date.UTC(Number(to.slice(0, 4)), Number(to.slice(5, 7)) - 1, Number(to.slice(8, 10)));
  return Math.floor((end - start) / 86_400_000) + 1;
}

/**
 * Masalah pada rentang tanggal, `null` bila sah.
 *
 * Batas 31 hari mencerminkan `MAX_SUMMARY_RANGE_DAYS` di server (`ATT-011`). Dicegah lebih dulu
 * di klien supaya manajer tidak menunggu perjalanan HTTP hanya untuk diberi tahu rentangnya
 * terlalu lebar — tetapi penolakan server tetap ditangani, karena batas itu milik server.
 */
export function validateRange(range: DateRange): RangeProblem | null {
  if (!DATE_PATTERN.test(range.from) || !DATE_PATTERN.test(range.to)) {
    return { code: 'invalid_format', message: 'Pilih tanggal awal dan akhir.' };
  }
  if (range.from > range.to) {
    return { code: 'inverted', message: 'Tanggal awal harus sebelum tanggal akhir.' };
  }
  const days = daysBetweenInclusive(range.from, range.to);
  if (days > MAX_SUMMARY_RANGE_DAYS) {
    return {
      code: 'too_long',
      message: `Rentang maksimum ${MAX_SUMMARY_RANGE_DAYS} hari; Anda memilih ${days} hari. Persempit rentangnya.`,
    };
  }
  return null;
}

// ── Filter tugas & ekspor ──────────────────────────────────────────────────

export interface TaskFilter {
  /** Kosong berarti "semua status". */
  readonly status: string;
}

/**
 * Query string untuk daftar DAN untuk ekspor — satu fungsi, dua pemakai.
 *
 * Justru penyatuan ini yang mencegah regresinya: tidak ada tempat kedua untuk merakit parameter,
 * jadi tidak ada tempat yang bisa lupa menyertakan filter.
 */
export function taskQuery(filter: TaskFilter): string {
  const params = new URLSearchParams();
  if (filter.status.trim().length > 0) {
    params.set('status', filter.status.trim());
  }
  return params.toString();
}

export function taskListPath(filter: TaskFilter): string {
  const query = taskQuery(filter);
  return query.length > 0 ? `/api/field-tasks?${query}` : '/api/field-tasks';
}

export function taskExportPath(filter: TaskFilter): string {
  const query = taskQuery(filter);
  return query.length > 0 ? `/api/field-tasks/export?${query}` : '/api/field-tasks/export';
}

/**
 * Kolom yang BENAR-BENAR ada di berkas ekspor — cerminan `FIELD_TASK_EXPORT_HEADER` server.
 *
 * Ditulis di sini supaya layar dapat menjanjikan isi berkas dengan jujur. Kontak, telepon, dan
 * koordinat tujuan SENGAJA tidak diekspor (ADR-0036 D5 minimisasi): berkas ekspor berpindah
 * tangan, dan data yang tidak dibutuhkan laporan operasional tidak ikut keluar.
 */
export const EXPORT_COLUMNS: readonly string[] = [
  'task_number',
  'title',
  'status',
  'priority',
  'assignee_user_id',
  'scheduled_date',
  'destination_name',
  'destination_address',
  'external_ref_source_system',
  'external_ref_entity_type',
  'external_ref_entity_id',
  'retry_of_task_id',
  'created_at',
  'completed_at',
];

/** Kolom yang sengaja TIDAK diekspor — dipakai layar untuk menyatakannya apa adanya. */
export const EXCLUDED_FROM_EXPORT: readonly string[] = [
  'destination_contact_name',
  'destination_contact_phone',
  'destination_latitude',
  'destination_longitude',
];

export function isExported(column: string): boolean {
  return EXPORT_COLUMNS.includes(column);
}

// ── Tugas pengganti ────────────────────────────────────────────────────────

export interface FailedTaskSource {
  readonly id: string;
  readonly taskTypeCode: string;
  readonly title: string;
  readonly priority: string;
  readonly destinationName: string | null;
  readonly destinationAddress: string | null;
  readonly instructions: string | null;
}

export interface ReplacementTaskDraft {
  readonly taskTypeCode: string;
  readonly title: string;
  readonly priority: string;
  readonly destinationName: string | null;
  readonly destinationAddress: string | null;
  readonly instructions: string | null;
  readonly retryOfTaskId: string;
}

/**
 * Menyalin tugas gagal menjadi draf tugas pengganti.
 *
 * `retryOfTaskId` WAJIB terisi — tanpa itu, tugas pengganti terlihat sebagai pekerjaan baru dan
 * hubungan sebab-akibatnya hilang dari laporan. Yang TIDAK disalin: status (pengganti selalu
 * mulai dari awal) dan assignee (kurir yang gagal belum tentu yang tepat untuk mencoba lagi —
 * itu keputusan dispatcher, bukan bawaan).
 */
export function buildReplacementTask(source: FailedTaskSource): ReplacementTaskDraft {
  return {
    taskTypeCode: source.taskTypeCode,
    title: source.title,
    priority: source.priority,
    destinationName: source.destinationName,
    destinationAddress: source.destinationAddress,
    instructions: source.instructions,
    retryOfTaskId: source.id,
  };
}
